import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { debounce } from 'lodash';

import { useMediaPredicate } from 'react-media-hook';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import { usePageTitle } from 'utils/pageTitle';
import { actions } from 'data/store';
import { dataConfig } from 'data/dataConfig';
import { prettifyDataSource, getAppRoute } from 'data/dataSourceConstants';
import {
	hasPrimarySourceDropdown,
	getPrimarySourcedMenuConfigForDataSet,
	getPrimarySourcedMenuLabel,
} from 'utils/primarySourcedDataSets';
import { fetchActiveDataSetTable } from 'utils/dataSetFetchUtils';
import DataTable from 'components/dataTable/DataTable';
import DataSourceSelect from 'components/dataTable/DataSourceSelect';
import FilterButtons from 'components/dataTable/FilterButtons';
import OwnerFilterDropdown from 'components/dataTable/OwnerFilterDropdown';
import Paginator from 'components/Paginator';
import Spinner from 'components/Spinner';
import OutboundLink from 'components/OutboundLink';

import './DataTablePage.css';

function DataTablePage({ dataSet, reducerName, dataSource, title, columns }) {
	let dispatch = useDispatch();
	const history = useHistory();
	const { address } = useParams();
	const isMobile = useMediaPredicate(MOBILE_SIZE);
	let {
		propertyId,
		isLoading,
		data,
		currentPage,
		totalPages,
		filteredStatus,
		sortField,
		sortDirection,
		numOpen,
		numClosed,
		dataSetUrl,
		emptyStateMessage,
		propertyData,
		searchString: reduxSearchString,
	} = useSelector((state) => ({
		propertyId: state.property.propertyData?.id,
		isLoading:
			state[reducerName].isLoading ||
			state.property.isLoading ||
			state.address.isLoading,
		data: state[reducerName].data,
		currentPage: state[reducerName].currentPage,
		totalPages: state[reducerName].totalPages,
		filteredStatus: state[reducerName].filteredStatus,
		sortField: state[reducerName].sortField,
		sortDirection: state[reducerName].sortDirection,
		numOpen: state[reducerName].numOpen,
		numClosed: state[reducerName].numClosed,
		dataSetUrl: state[reducerName].dataSetUrl,
		emptyStateMessage: state[reducerName].emptyStateMessage,
		propertyData: state.property.propertyData,
		searchString: state[reducerName].searchString,
	}));

	let [searchString, setSearchString] = useState('');
	let [ownerFilter, setOwnerFilter] = useState('all');

	useEffect(() => {
		setSearchString(reduxSearchString || '');
	}, [reduxSearchString]);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!propertyId) return;
		fetchActiveDataSetTable(dispatch, actions, {
			propertyId,
			dataSet,
			reducerName,
			filteredStatus,
			searchString: reduxSearchString,
		});
	}, [propertyId, reducerName, dataSet]);

	let subtitle = `(${prettifyDataSource(dataSource)})`;
	const isFdnyPermits2 = dataSet === 'fdnyPermits2';
	let showStatusFilter = dataConfig[dataSet].hasStatus !== false && !isFdnyPermits2;
	const showPrimarySourceDropdown = hasPrimarySourceDropdown(dataSet);
	const menuConfig = getPrimarySourcedMenuConfigForDataSet(dataSet);
	const displayTitle = menuConfig
		? getPrimarySourcedMenuLabel(menuConfig)
		: title;
	const openLabel = isFdnyPermits2 ? 'Expired' : (dataSet === 'bisIlluminatedSigns' ? 'Expired' : 'Open');
	//dotLiens closes by the clerk recording a release, so 'Released' is the accurate label
	const closedLabel = isFdnyPermits2 ? 'Active' : (dataSet === 'bisIlluminatedSigns' ? 'Active' : (dataSet === 'dotLiens' ? 'Released' : 'Resolved'));
	const statusForFetch = isFdnyPermits2 ? 'all' : filteredStatus;

	const onSourceChange = (nextDataSet, nextSource) => {
		if (nextDataSet !== dataSet || nextSource !== dataSource) {
			history.push(
				`/property/${address}/${getAppRoute(nextDataSet, nextSource)}`,
			);
		}
	};

	usePageTitle(displayTitle, propertyData);

	const ownerOptions = useMemo(() => {
		if (!isFdnyPermits2 || !Array.isArray(data)) {
			return [];
		}
		const normalizedToLabel = new Map();
		data.forEach((row) => {
			const ownerName = (row?.owner_name || '').trim();
			if (!ownerName) return;
			const normalized = ownerName.toLowerCase();
			if (!normalizedToLabel.has(normalized)) {
				normalizedToLabel.set(normalized, ownerName);
			}
		});
		return Array.from(normalizedToLabel.values()).sort((a, b) =>
			a.localeCompare(b),
		);
	}, [data, isFdnyPermits2]);

	useEffect(() => {
		if (!isFdnyPermits2) return;
		if (ownerFilter === 'all') return;
		const stillExists = ownerOptions.includes(ownerFilter);
		if (!stillExists) {
			setOwnerFilter('all');
		}
	}, [isFdnyPermits2, ownerFilter, ownerOptions]);

	const filteredData = useMemo(() => {
		if (!isFdnyPermits2 || ownerFilter === 'all') {
			return data;
		}
		if (!Array.isArray(data)) {
			return data;
		}
		const target = ownerFilter.trim().toLowerCase();
		return data.filter((row) => {
			const ownerName = (row?.owner_name || '').trim().toLowerCase();
			return ownerName === target;
		});
	}, [data, isFdnyPermits2, ownerFilter]);

	const debouncedSearch = debounce((searchValue) => {
		setSearchString(searchValue);
		dispatch(
			actions[reducerName].fetchData(
				propertyId,
				statusForFetch,
				1,
				searchValue,
			),
		);
	}, 400);

	return (
		<div
			className={`data-table-container ${isMobile ? 'data-table-container-mobile' : ''}`}
		>
			<nav className="navbar navbar-expand-lg navbar-light bg-light">
				<div className="container-fluid">
					<div className="d-flex align-items-center flex-wrap">
						<span className="navbar-brand data-set-name">{displayTitle}</span>
						{showPrimarySourceDropdown ? (
							<DataSourceSelect
								dataSet={dataSet}
								dataSource={dataSource}
								propertyData={propertyData}
								onSourceChange={onSourceChange}
								isDisabled={isLoading}
							/>
						) : (
							<span className="navbar-text data-source-name">
								<OutboundLink url={dataSetUrl}>{subtitle}</OutboundLink>
							</span>
						)}
					</div>
					<div className="table-controls">
						<input
							type="text"
							className="form-control data-table-search me-3"
							placeholder={`Search ${title}`}
							value={searchString}
							onChange={(e) => debouncedSearch(e.target.value)}
						/>
						{isFdnyPermits2 && (
							<OwnerFilterDropdown
								onFilter={(owner) => setOwnerFilter(owner)}
								filteredOwner={ownerFilter}
								owners={ownerOptions}
								isDisabled={isLoading}
							/>
						)}
						{showStatusFilter && (
							<FilterButtons
								onFilter={(status) =>
									dispatch(
										actions[reducerName].fetchData(
											propertyId,
											status,
											1,
											searchString,
										),
									)
								}
								filteredStatus={filteredStatus}
								numOpen={numOpen}
								numClosed={numClosed}
								openLabel={openLabel}
								closedLabel={closedLabel}
							/>
						)}
					</div>
				</div>
			</nav>
			{propertyData && isLoading && <Spinner />}
			{!isLoading && data?.length === 0 && emptyStateMessage && (
				<div className="alert alert-info" role="alert">
					{emptyStateMessage}
				</div>
			)}
			<div className="table-wrapper">
				<DataTable
					columns={columns}
					data={filteredData}
					dataSet={dataSet}
					dataSource={dataSource}
					propertyData={propertyData}
					isLoading={isLoading}
					searchString={searchString}
					sortField={sortField}
					sortDirection={sortDirection}
					onSort={(nextSort) =>
						dispatch(
							actions[reducerName].fetchData(
								propertyId,
								statusForFetch,
								1,
								searchString,
								nextSort,
							),
						)
					}
				/>
			</div>
			<Paginator
				currentPage={currentPage}
				totalPages={totalPages}
				setPage={(pageNum) => dispatch(actions[reducerName].setPage(pageNum))}
			/>
		</div>
	);
}

export default DataTablePage;
